import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Location, useLocation, useNavigate } from "react-router";
import { VelavuAPI } from "velavu-js-api";
import IconAdd from "../../../dynamicicons/icon-add";
import AssetAddWizard from "../../../elements/asset-add-wizard";
import * as wizardStyles from "../../../elements/asset-add-wizard.module.scss";
import { CategoryTag, GroupTag } from "../../../elements/categorization-tag";
import DynamicBattery from "../../../elements/dynamic-battery";
import StatusIndicator from "../../../elements/status-indicator";
import VelavuArrowButton from "../../../elements/velavu-arrow-button";
import VelavuButton from "../../../elements/velavu-button";
import VelavuItemIcon from "../../../elements/velavu-item-icon";
import VelavuSimpleTable, {
	CenteredTD,
	MultilineTD,
} from "../../../elements/velavu-simple-table";
import { getBase64FromUrl } from "../../../helper/image-helper";
import { mapConnectionStatus } from "../../../helper/language-helper";
import { getDeviceStatus } from "../../../helper/status-helper";
import { AssetAddData } from "./asset-add-data";
import * as styles from "./asset-new-confirm.module.scss";

export default function AssetNewConfirm() {
	const { t } = useTranslation();
	const location = useLocation() as Location<{
		wizardData: AssetAddData;
	} | null>;
	const wizardData = location?.state?.wizardData;

	const [isSubmitting, setSubmitting] = useState(false);
	const navigate = useNavigate();

	const status = useMemo(() => {
		return getDeviceStatus(wizardData?.tag);
	}, [wizardData?.tag]);

	const submitAssetImage = useCallback((base64Data: string, id: string) => {
		VelavuAPI.assets
			.uploadProfilePicture(id, base64Data)
			.catch((error) => console.log(error));
	}, []);

	const submitAsset = useCallback(() => {
		if (isSubmitting) return;

		setSubmitting(true);

		VelavuAPI.assets
			.createNewAsset({
				name: wizardData!.asset.assetName,
				category: wizardData!.asset.assetCategorization.category,
				group: wizardData!.asset.assetCategorization.group,
				notes: wizardData!.asset.assetNotes,
				device_id: wizardData!.tag?.id,
			})
			.then((response) => {
				if (wizardData!.asset.assetImage) {
					getBase64FromUrl(wizardData!.asset.assetImage).then(
						(base64Data) =>
							submitAssetImage(base64Data!, response.id),
					);
				}
			})
			.finally(() => navigate("/assets"))
			.catch((error) => {
				console.log(error);
				setSubmitting(false);
			});
	}, [isSubmitting, wizardData, submitAssetImage, navigate]);

	return (
		<AssetAddWizard
			previous={
				<Link
					className={wizardStyles.navButton}
					to="/assets/create/tag"
					state={{ wizardData: wizardData }}
				>
					<VelavuArrowButton direction="left" />
				</Link>
			}
			next={
				<div className={wizardStyles.navButton}>
					<VelavuArrowButton
						onClick={submitAsset}
						direction="confirm"
						disabled={isSubmitting}
						loading={isSubmitting}
					/>
				</div>
			}
			stageCount={3}
			currentStage={2}
		>
			<div style={{ padding: 20 }}>
				<h1>{t("asset_detail.create.overview")}</h1>
				<div className={styles.container}>
					{wizardData ? (
						<ResultsTile
							type={t("asset_detail.create.tile_asset")}
							editPath="/assets/create"
							editState={{ wizardData: wizardData }}
							icon={
								wizardData.asset.assetImage ? (
									<img
										className={styles.assetImagePreview}
										src={wizardData.asset.assetImage}
										alt=""
									/>
								) : (
									<VelavuItemIcon
										data={{
											category:
												wizardData.asset
													.assetCategorization
													.category,
										}}
										status={status}
									/>
								)
							}
							title={wizardData.asset.assetName}
						>
							<VelavuSimpleTable>
								<tbody>
									<tr>
										<td>
											{t("asset_detail.create.category")}
										</td>
										<td>
											<CategoryTag
												category={
													wizardData.asset
														.assetCategorization
														.category
												}
											/>
										</td>
									</tr>
									<tr>
										<td>
											{t("asset_detail.create.group")}
										</td>
										<td>
											<GroupTag
												categorization={
													wizardData.asset
														.assetCategorization
												}
											/>
										</td>
									</tr>
									<tr>
										<td>
											{t("asset_detail.create.notes")}
										</td>
										<MultilineTD>
											{wizardData.asset.assetNotes
												? wizardData.asset.assetNotes
												: t("asset_detail.create.none")}
										</MultilineTD>
									</tr>
								</tbody>
							</VelavuSimpleTable>
						</ResultsTile>
					) : (
						<AbsentTile
							message={t("asset_detail.create.no_asset_paired")}
							action={t("asset_detail.create.pair_asset")}
							editPath="/assets/create"
							editState={{ wizardData: wizardData }}
						/>
					)}
					<div className={styles.tileSeparator} />

					{wizardData?.tag ? (
						<ResultsTile
							type={t("asset_detail.create.tile_tag")}
							editPath="/assets/create/tag"
							editState={{ wizardData: wizardData }}
							icon={
								<VelavuItemIcon
									data={{ model: wizardData.tag.model }}
								/>
							}
							title={wizardData.tag.id}
						>
							<VelavuSimpleTable>
								<tbody>
									<tr>
										<td>
											{t("asset_detail.create.status")}
										</td>
										<CenteredTD>
											<StatusIndicator status={status} />
											<span style={{ marginLeft: 8 }}>
												{mapConnectionStatus(status, t)}
											</span>
										</CenteredTD>
									</tr>
									<tr>
										<td>
											{t(
												"asset_detail.create.table.battery",
											)}
										</td>
										<CenteredTD>
											<span style={{ marginRight: 4 }}>
												{wizardData.tag.state?.power
													?.battery_level !==
												undefined
													? `${wizardData.tag.state.power.battery_level}%`
													: t("common.na")}
											</span>
											<DynamicBattery
												resource={
													wizardData.tag.state?.power
												}
											/>
										</CenteredTD>
									</tr>
								</tbody>
							</VelavuSimpleTable>
						</ResultsTile>
					) : (
						<AbsentTile
							message={t("asset_detail.create.no_tag_paired")}
							action={t("asset_detail.create.pair_tag")}
							editPath="/assets/create/tag"
							editState={{ wizardData: wizardData }}
						/>
					)}
				</div>
			</div>
		</AssetAddWizard>
	);
}

function AbsentTile(props: {
	message: string;
	action: string;
	editPath: string;
	editState?: Record<string, unknown>;
}) {
	return (
		<div className={`${styles.tile} ${styles.absentTile}`}>
			<h3>{props.message}</h3>
			<Link
				style={{ textDecoration: "none" }}
				to={props.editPath}
				state={props.editState}
			>
				<VelavuButton
					icon={(size, color, className) => (
						<IconAdd
							size={size}
							color={color}
							className={className}
						/>
					)}
					label={props.action}
				/>
			</Link>
		</div>
	);
}

interface ResultsTileProps {
	type: string;
	editPath: string;
	editState?: Record<string, unknown>;
	icon: React.ReactNode;
	title: string;
	children?: React.ReactNode;
}

function ResultsTile(props: ResultsTileProps) {
	const { t } = useTranslation();

	return (
		<div className={styles.tile}>
			<div className={styles.tileTitle}>
				<h3>{props.type}</h3>

				<Link
					style={{ textDecoration: "none" }}
					to={props.editPath}
					state={props.editState}
				>
					<VelavuButton
						label={t("common.edit")}
						size="small"
						variant="outlined"
					/>
				</Link>
			</div>

			<div className={styles.tileHeader}>
				{props.icon}
				<h3 className={styles.headerTitle}>{props.title}</h3>
			</div>

			{props.children}
		</div>
	);
}
